import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BadgeCheck, Truck, Store, MapPin, ArrowLeft, Plus, Leaf, Calendar, ShoppingCart, Flag, Loader2 } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : null;

export default function FarmProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, count } = useCart();
  const [farm, setFarm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportEmail, setReportEmail] = useState("");
  const [reporting, setReporting] = useState(false);

  const submitReport = async () => {
    if (reportReason.trim().length < 3) { toast.error("Please tell us briefly what the problem is."); return; }
    setReporting(true);
    try {
      const res = await api.post(`/listings/${id}/report`, { reason: reportReason.trim(), reporter_email: reportEmail.trim() });
      toast.success(res.data.message || "Thanks for letting us know.");
      setReportOpen(false); setReportReason(""); setReportEmail("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send your report. Please try again.");
    } finally { setReporting(false); }
  };

  useEffect(() => {
    api.get(`/farms/${id}`)
      .then((res) => setFarm(res.data))
      .catch(() => toast.error("Producer not found"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div className="pt-16"><Skeleton className="h-72 w-full" /><div className="max-w-6xl mx-auto p-6 space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-40 w-full" /></div></div>;
  }
  if (!farm) return <div className="pt-32 text-center text-muted-foreground">Producer not found.</div>;

  const cert = farm.certification || {};
  const certVerified = cert.verified;

  const addToCart = (p) => {
    if (p.stock_quantity <= 0) { toast.error("Out of stock"); return; }
    addItem(p, farm, 1);
    toast.success(`${p.name} added to cart`);
  };

  return (
    <div className="pt-16 min-h-screen pb-24">
      {/* Banner */}
      <div className="relative h-64 sm:h-80 bg-secondary">
        {farm.photo_url ? (
          <img src={imgUrl(farm.photo_url)} alt={farm.farm_name} className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full grid place-items-center text-muted-foreground bg-gradient-to-b from-[#e7ecdf] to-[#dfe7d8]"><Store className="h-12 w-12" /></div>
        )}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(31,42,34,0) 40%, rgba(31,42,34,0.75) 100%)" }} />
        <Button variant="secondary" size="sm" className="absolute top-4 left-4" onClick={() => navigate(-1)} data-testid="farm-back-button">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {farm.seller_type && <Badge className="bg-card/95 text-foreground border" data-testid="farm-profile-seller-type">{farm.seller_type}</Badge>}
              {certVerified && <Badge className="bg-[#E6F2E8] text-[#1F5A2F] border border-[rgba(31,90,47,0.25)]" data-testid="farm-profile-verified-badge"><BadgeCheck className="h-3.5 w-3.5 mr-1" />{cert.label} · Verified</Badge>}
              {!certVerified && cert.cert_type === "in_conversion" && <Badge className="bg-[#FBF3E2] text-[#7A5A1F] border">In Conversion to Organic</Badge>}
              {farm.collection_available && <Badge className="bg-[#F3EEE6] text-[#5A3A1F] border"><Store className="h-3 w-3 mr-1" />Collection</Badge>}
              {farm.delivery_available && <Badge className="bg-[#E7F1F3] text-[#1F4E57] border"><Truck className="h-3 w-3 mr-1" />Delivery</Badge>}
            </div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-white" data-testid="farm-profile-title">{farm.farm_name}</h1>
            <p className="text-white/85 flex items-center gap-1 mt-1 text-sm"><MapPin className="h-4 w-4" /> {farm.town}{farm.county ? `, ${farm.county}` : ""} {farm.distance != null && `· ${farm.distance} mi away`}</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
        <div>
          {farm.description && <p className="text-[15px] leading-relaxed text-foreground/90 mb-6">{farm.description}</p>}
          <h2 className="text-xl font-semibold mb-4">Available produce</h2>
          {farm.products.length === 0 ? (
            <Card className="p-8 text-center text-muted-foreground rounded-2xl">No products listed yet.</Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="farm-profile-product-grid">
              {farm.products.map((p) => (
                <Card key={p.id} className="rounded-2xl overflow-hidden hover:shadow-md transition-shadow" data-testid="product-card">
                  <div className="relative h-44 bg-secondary">
                    {p.image_url ? <img src={imgUrl(p.image_url)} alt={p.name} className="h-full w-full object-cover" /> : <div className="h-full grid place-items-center text-muted-foreground">No image</div>}
                    <div className="absolute top-2 left-2 flex gap-1.5">
                      <Badge variant="secondary" className="bg-card/90">{p.category}</Badge>
                    </div>
                    {p.stock_quantity <= 0 && <div className="absolute inset-0 bg-black/40 grid place-items-center"><Badge className="bg-red-100 text-red-800">Out of stock</Badge></div>}
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold leading-tight">{p.name}</h3>
                      <div className="text-right shrink-0">
                        <div className="font-semibold text-primary">£{p.price.toFixed(2)}</div>
                        <div className="text-xs text-muted-foreground">{p.unit}</div>
                      </div>
                    </div>
                    {p.description && <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{p.description}</p>}
                    {p.sourced_from && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1.5" data-testid="product-sourced-from">
                        <MapPin className="h-3 w-3" /> Sourced from: {p.sourced_from}
                      </p>
                    )}
                    {(p.production_tags?.length > 0) && (
                      <div className="mt-2.5" data-testid="product-production-tags-display">
                        <div className="flex flex-wrap gap-1.5">
                          {p.production_tags.map((t) => (
                            <span key={t} className="inline-flex items-center gap-1 rounded-full bg-[#EEF4E6] text-[#3E5A1F] border border-[#cfe0bc] px-2 py-0.5 text-[11px]">
                              <Leaf className="h-3 w-3" />{t}
                            </span>
                          ))}
                        </div>
                        <p className="text-[10px] text-muted-foreground italic mt-1">Production details described by the producer.</p>
                      </div>
                    )}
                    {(p.available_from || p.available_until) && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-2"><Calendar className="h-3 w-3" /> {fmtDate(p.available_from)} – {fmtDate(p.available_until)}</p>
                    )}
                    <Button className="w-full mt-3" onClick={() => addToCart(p)} disabled={p.stock_quantity <= 0} data-testid="product-add-to-cart-button">
                      <Plus className="h-4 w-4 mr-1" /> Add to cart
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Right rail */}
        <div className="space-y-4">
          <Card className="rounded-2xl p-5 sticky top-24">
            <h3 className="font-semibold mb-3">Producer details</h3>
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-muted-foreground text-xs uppercase tracking-wide">Address</div>
                <div>{[farm.address_line1, farm.address_line2, farm.town, farm.county, farm.postcode].filter(Boolean).join(", ")}</div>
              </div>
              {farm.collection_available && (
                <div><div className="text-muted-foreground text-xs uppercase tracking-wide flex items-center gap-1"><Store className="h-3 w-3" />Collection</div><div>{farm.collection_info || "Available"}</div></div>
              )}
              {farm.delivery_available && (
                <div><div className="text-muted-foreground text-xs uppercase tracking-wide flex items-center gap-1"><Truck className="h-3 w-3" />Delivery</div><div>Within {farm.delivery_radius_miles} miles{farm.delivery_fee ? ` · £${Number(farm.delivery_fee).toFixed(2)} fee` : " · Free"}</div></div>
              )}
              {cert.cert_type && cert.cert_type !== "none" && (
                <><Separator /><div>
                  <div className="text-muted-foreground text-xs uppercase tracking-wide flex items-center gap-1">
                    {certVerified ? <BadgeCheck className="h-3 w-3 text-[#1F5A2F]" /> : null} Certification
                  </div>
                  <div className="flex items-center gap-1.5">
                    {certVerified && <BadgeCheck className="h-4 w-4 text-[#1F5A2F]" />}
                    <span>{cert.label}{cert.body && cert.cert_type === "other" ? "" : (cert.body ? ` (${cert.body})` : "")}</span>
                  </div>
                  {cert.certificate_number && <div className="text-xs text-muted-foreground">Cert no. {cert.certificate_number}{cert.expiry_date ? ` · expires ${cert.expiry_date}` : ""}</div>}
                  {!certVerified && <div className="text-xs text-muted-foreground italic">Self-declared — no certificate number provided.</div>}
                </div></>
              )}
            </div>
            {count > 0 && (
              <Button className="w-full mt-4 bg-[color:var(--fg-wood-600)] hover:bg-[color:var(--fg-wood-700)] text-white" onClick={() => navigate("/cart")}>
                <ShoppingCart className="h-4 w-4 mr-1" /> View cart ({count})
              </Button>
            )}
          </Card>

          <Dialog open={reportOpen} onOpenChange={setReportOpen}>
            <DialogTrigger asChild>
              <button className="mt-3 mx-auto flex items-center gap-1.5 text-xs text-muted-foreground hover:text-red-600 transition-colors" data-testid="report-listing-trigger">
                <Flag className="h-3.5 w-3.5" /> Report this listing
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-md z-[1100]" data-testid="report-dialog">
              <DialogHeader><DialogTitle className="flex items-center gap-2"><Flag className="h-4 w-4 text-red-600" /> Report this listing</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">Spotted something wrong or misleading? Let us know and our team will take a look.</p>
                <div className="space-y-1.5">
                  <Label>What's the problem?</Label>
                  <Textarea rows={3} value={reportReason} onChange={(e) => setReportReason(e.target.value)} placeholder="e.g. This producer no longer trades, or a claim looks inaccurate." data-testid="report-reason-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Your email (optional)</Label>
                  <Input type="email" value={reportEmail} onChange={(e) => setReportEmail(e.target.value)} placeholder="So we can follow up if needed" data-testid="report-email-input" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setReportOpen(false)}>Cancel</Button>
                <Button onClick={submitReport} disabled={reporting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="report-submit-button">
                  {reporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Flag className="h-4 w-4 mr-1" />} Send report
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
